
import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import BackofficeSidebar from './BackofficeSidebar';
import BackofficeNavbar from './BackofficeNavbar';
import Footer from '@/components/homepage/Footer';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { ProfileService } from '@/services/profileService';
import { useStableAuth } from '@/hooks/useStableAuth';

interface BackofficeLayoutProps {
  children: React.ReactNode;
}

const BackofficeLayout = ({ children }: BackofficeLayoutProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { userRole } = useAuth();
  const { user } = useStableAuth();

  const finalUserRole = userRole === 'admin' ? 'admin' : 'user';

  // Fetch max_levels for conditional sidebar visibility
  const { data: userProfile } = useQuery({
    queryKey: ['enhanced-user-profile-layout', user?.id],
    queryFn: () => ProfileService.getEnhancedUserProfile(),
    staleTime: 10 * 60 * 1000,
    enabled: !!user?.id && finalUserRole === 'user',
  });

  const maxLevels = userProfile?.max_levels ?? null;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="min-h-screen flex w-full">
      <div className="hidden lg:block fixed left-0 top-0 h-screen z-30 w-80">
        <BackofficeSidebar userRole={finalUserRole} maxLevels={maxLevels} />
      </div>

      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          onClick={toggleMobileMenu}
          variant="outline"
          size="icon"
          className="bg-white shadow-md"
        >
          {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      {isMobileMenuOpen && (
        <>
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="lg:hidden fixed left-0 top-0 h-full z-50 animate-slide-in-left">
            <BackofficeSidebar userRole={finalUserRole} maxLevels={maxLevels} />
          </div>
        </>
      )}

      <div className="flex-1 lg:ml-80">
        <div className="flex-shrink-0 sticky top-0 z-20 bg-white shadow-sm">
          <BackofficeNavbar />
        </div>

        <main className="bg-gray-50 backoffice-main-content min-h-[calc(100vh-64px)]">
          {children}
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default BackofficeLayout;
